/**
 * @fileoverview Application state persistence using localStorage.
 * Provides a clean API for saving and restoring user preferences, map state,
 * and UI settings between sessions.
 * @module state/StateManager
 */


import { MAPAPP_DEFAULTS, SIDEBAR_MIN_WIDTH, SIDEBAR_MAX_WIDTH } from '../constants.js';
import { formatSizeAsNiceSizeString, showStatusWarning } from '../utils/jrfuncs.js';


/**
 * Declarative accessor definitions for auto-generating getter/setter methods.
 * Each row defines: method name suffix, state path, and default value.
 * @type {Array<{name: string, path: string, default: *}>}
 */
const STATE_ACCESSORS = [
    { name: 'TileLayerIndex', path: 'map.tileLayerIndex', default: 0 },
    { name: 'SidebarTab', path: 'sidebar.activeTab', default: 'white-tab' },
    { name: 'SidebarWidth', path: 'sidebar.width', default: null },
    { name: 'SidebarCollapsed', path: 'sidebar.collapsed', default: false },
    { name: 'HelpLastTopic', path: 'help.lastTopicId', default: null },
    { name: 'HelpSearchQuery', path: 'help.searchQuery', default: '' },
    { name: 'AdvancedSearchEnabled', path: 'settings.advancedSearchEnabled', default: false },
    { name: 'InactiveLayerOpacity', path: 'settings.inactiveLayerOpacity', default: MAPAPP_DEFAULTS.INACTIVE_LAYER_OPACITY },
    { name: 'OutsideRegionOpacity', path: 'settings.outsideRegionOpacity', default: MAPAPP_DEFAULTS.OUTSIDE_REGION_OPACITY },
    { name: 'SelectedRegion', path: 'map.selectedRegion', default: '' },
    { name: 'RestrictSearchToRegion', path: 'settings.restrictSearchToRegion', default: true },
    { name: 'SortByAnchorDistance', path: 'settings.sortByAnchorDistance', default: false },
    { name: 'LastExportFormat', path: 'settings.lastExportFormat', default: null },
    { name: 'Theme', path: 'settings.theme', default: 'normal' },
    { name: 'ShowExtraPcatInfo', path: 'settings.showExtraPcatInfo', default: false },
    { name: 'YellowMarkerStyleId', path: 'settings.yellowMarkerStyleId', default: MAPAPP_DEFAULTS.DEFAULT_MARKER_STYLE },
    { name: 'MapEffect', path: 'map.effect', default: 'none' },
    { name: 'LastPdfUrl', path: 'pdf.lastUrl', default: '' },
    { name: 'PdfUrlHistory', path: 'pdf.urlHistory', default: [] },
    { name: 'PlayLogText', path: 'playlog.text', default: '' },
    { name: 'SidebarFullWidth', path: 'sidebar.fullWidth', default: false },
    { name: 'HideLogPanel', path: 'settings.hideLogPanel', default: false },
    { name: 'ShowExtraActionButtons', path: 'settings.showExtraActionButtons', default: false },
    { name: 'StayOnMapAfterReadButtonPress', path: 'settings.stayOnMapAfterReadButtonPress', default: false },
    { name: 'PdfAutoHighlightOnCopy', path: 'settings.pdfAutoHighlightOnCopy', default: true },
    { name: 'PdfPopupMediaFiles', path: 'settings.pdfPopupMediaFiles', default: true },
    { name: 'RightMarginGutter', path: 'settings.rightMarginGutter', default: 0 },
    { name: 'FitSizeZoom', path: 'settings.fitSizeZoom', default: 0 },
    { name: 'PlayLogFontSize', path: 'settings.playLogFontSize', default: 12 },
];


/**
 * Builds the default world "ui" state structure (a fresh object every call).
 * @returns {Object} Default state object with map, sidebar, help, settings and overlays sections
 */
function buildDefaultUiState() {
    return {
        map: {
            tileLayerIndex: 0,
            zoom: null,
            center: null
        },
        sidebar: {
            activeTab: 'white-tab',
            width: null,
            collapsed: false
        },
        help: {
            lastTopicId: null,
            searchQuery: ''
        },
        settings: {
            advancedSearchEnabled: false,
            inactiveLayerOpacity: MAPAPP_DEFAULTS.INACTIVE_LAYER_OPACITY,
            outsideRegionOpacity: MAPAPP_DEFAULTS.OUTSIDE_REGION_OPACITY,
            yellowMarkerStyleId: MAPAPP_DEFAULTS.DEFAULT_MARKER_STYLE
        },
        overlays: {
            white: false,
            yellow: false,
            reverse: false,
            advanced: false,
            bookmarks: true,
            history: false
        }
    };
}


/**
 * The stored values whose allowed shape is narrower (or, for legacy values, wider) than "the type of
 * the STATE_ACCESSORS default". Each rule returns {ok: true, value} with the value to keep (possibly
 * normalized) or {ok: false, reason}.
 * @type {Object<string, function(*): {ok: boolean, value?: *, reason?: string}>}
 */
const UI_STATE_PATH_RULES = {
    // '' = no region. Worlds loaded before 10/2026 stored null for "no region" (MapApp passed null
    // through on every world load), which is read as '' rather than reported as a wrong type.
    'map.selectedRegion': (v) => {
        if (v === null) return { ok: true, value: '' };
        return (typeof v === 'string' && v.length <= 200) ? { ok: true, value: v } : { ok: false, reason: 'not a region id' };
    },
    // the id of a sidebar tab button, as the tab listener writes it ('white-tab', ...); something that
    // cannot be an element id is dropped here (and reported by a backup import), while
    // UiManager.restoreSidebarTab still checks that the id really names one of the sidebar's tabs
    'sidebar.activeTab': (v) => {
        return (typeof v === 'string' && /^[A-Za-z][A-Za-z0-9_-]{0,63}$/.test(v))
            ? { ok: true, value: v }
            : { ok: false, reason: 'not a tab id' };
    },
    // the sidebar width in whole pixels, as SidebarResizer writes it (clamped to its min..max), or null
    'sidebar.width': (v) => {
        if (v === null) return { ok: true, value: null };
        return (Number.isInteger(v) && v >= SIDEBAR_MIN_WIDTH && v <= SIDEBAR_MAX_WIDTH)
            ? { ok: true, value: v }
            : { ok: false, reason: `not a width in pixels (${SIDEBAR_MIN_WIDTH}-${SIDEBAR_MAX_WIDTH})` };
    },
};


/**
 * Validates a world "ui" state object (the value stored under "<prefix>_ui"), both when it is read
 * back from localStorage (StateManager.loadState) and when it comes from a settings backup. The state
 * is REBUILT from the known paths only -- every STATE_ACCESSORS path, the map view (map.zoom /
 * map.center) and the overlay switches -- and a value of the wrong shape is dropped (so the default
 * applies): the default's type, or the narrower rule in UI_STATE_PATH_RULES. Unknown keys are left
 * out too, and listed separately: the load-time check ignores them (a setting a later version stopped
 * using is not an error), while the backup import reports them.
 * @param {*} value - The parsed value
 * @returns {{state: Object, droppedCount: number, dropped: Array<{path: string, reason: string}>, unknown: string[]}|null}
 *   The clean state, how many known values were dropped and why, and the unknown paths left out; or
 *   null if the value is not a state object at all
 */
export function sanitizeStoredUiState(value) {
    if (!value || typeof value !== 'object' || Array.isArray(value)) {
        return null;
    }
    // start from the defaults, so every section the app expects (map, sidebar, ...) exists
    const state = buildDefaultUiState();
    const dropped = [];
    const unknown = [];
    const isFiniteNumber = (v) => (typeof v === 'number' && Number.isFinite(v));
    const isPlainObject = (v) => (v && typeof v === 'object' && !Array.isArray(v));
    const getPath = (obj, path) => path.split('.').reduce((o, k) => ((o && typeof o === 'object' && Object.prototype.hasOwnProperty.call(o, k)) ? o[k] : undefined), obj);
    const setPath = (obj, path, v) => {
        const keys = path.split('.');
        let o = obj;
        for (const k of keys.slice(0, -1)) {
            if (!o[k] || typeof o[k] !== 'object') o[k] = {};
            o = o[k];
        }
        o[keys[keys.length - 1]] = v;
    };
    const keepIf = (path, isGood, reason) => {
        const v = getPath(value, path);
        if (v === undefined) return;
        const rule = UI_STATE_PATH_RULES[path];
        if (rule) {
            const outcome = rule(v);
            if (outcome.ok) {
                setPath(state, path, outcome.value);
            } else {
                dropped.push({ path, reason: outcome.reason });
            }
        } else if (isGood(v)) {
            setPath(state, path, v);
        } else {
            dropped.push({ path, reason });
        }
    };
    const matchesDefault = (v, def) => {
        if (def === null) return v === null || typeof v === 'string' || isFiniteNumber(v);
        if (Array.isArray(def)) return Array.isArray(v) && v.every(e => typeof e === 'string');
        if (typeof def === 'number') return isFiniteNumber(v);
        return typeof v === typeof def;
    };
    const describeDefaultType = (def) => {
        if (def === null) return 'not text or a number';
        if (Array.isArray(def)) return 'not a list of text';
        if (typeof def === 'number') return 'not a number';
        if (typeof def === 'boolean') return 'not true/false';
        return `not ${typeof def === 'string' ? 'text' : typeof def}`;
    };

    const knownPaths = new Set(['map.zoom', 'map.center']);
    for (const accessor of STATE_ACCESSORS) {
        knownPaths.add(accessor.path);
        keepIf(accessor.path, (v) => matchesDefault(v, accessor.default), describeDefaultType(accessor.default));
    }
    keepIf('map.zoom', (v) => v === null || isFiniteNumber(v), 'not a zoom level');
    keepIf('map.center', (v) => v === null || (Array.isArray(v) && v.length === 2 && v.every(isFiniteNumber)), 'not a map position');
    const overlays = value.overlays;
    if (isPlainObject(overlays)) {
        for (const [layerId, visible] of Object.entries(overlays)) {
            // a layer id is a plain name, never one of Object.prototype's ("constructor", "__proto__")
            if (/^[A-Za-z0-9_-]{1,40}$/.test(layerId) && !(layerId in Object.prototype) && typeof visible === 'boolean') {
                setPath(state, `overlays.${layerId}`, visible);
            } else {
                dropped.push({ path: `overlays.${layerId}`, reason: 'not a layer switch' });
            }
        }
    } else if (overlays !== undefined) {
        dropped.push({ path: 'overlays', reason: 'not a set of layer switches' });
    }

    // what is left over is not a setting of this app
    for (const [section, sectionValue] of Object.entries(value)) {
        if (section === 'overlays') continue;
        if (isPlainObject(sectionValue)) {
            for (const key of Object.keys(sectionValue)) {
                if (!knownPaths.has(`${section}.${key}`)) {
                    unknown.push(`${section}.${key}`);
                }
            }
        } else if (![...knownPaths].some(path => path.startsWith(section + '.'))) {
            unknown.push(section);
        } else {
            dropped.push({ path: section, reason: 'not a settings section' });
        }
    }
    return { state, droppedCount: dropped.length, dropped, unknown };
}


/**
 * Validates a background music list kept in app-scoped storage -- bgMusicUserTracks ({title, url}
 * tracks) or bgMusicPlaylists ({label, url} m3u playlists) -- both when StateManager reads it back
 * from localStorage and when it comes from a settings backup (ImportExportHelper.
 * validateSettingsBackup, which also passes length limits). An entry is kept only when it is an
 * object whose url (not blank) and title / label are strings; it is rebuilt from exactly those two
 * fields, the only ones the app writes. A stored null or non-object entry, or a list that is not a
 * list, used to make the first add/remove of a track or playlist throw.
 * @param {*} value - The parsed value
 * @param {string} textField - 'title' for tracks, 'label' for playlists
 * @param {{maxUrlLength?: number, maxTextLength?: number}} [limits={}] - Length limits (none by default)
 * @returns {{list: Array<Object>, dropped: Array<{index: number, field: string|null}>}|null}
 *   The clean list and what was left out, in order (field null = the whole entry, otherwise an extra
 *   field of a kept entry); or null if the value is not a list at all
 */
export function sanitizeStoredMusicList(value, textField, limits = {}) {
    if (!Array.isArray(value)) {
        return null;
    }
    const { maxUrlLength = Infinity, maxTextLength = Infinity } = limits;
    const isText = (v, max) => (typeof v === 'string') && (v.length <= max);
    const list = [];
    const dropped = [];
    value.forEach((entry, index) => {
        if (entry && typeof entry === 'object' && !Array.isArray(entry) && isText(entry.url, maxUrlLength) && entry.url.trim() && isText(entry[textField], maxTextLength)) {
            list.push({ [textField]: entry[textField], url: entry.url });
            for (const field of Object.keys(entry)) {
                if (field !== textField && field !== 'url') {
                    dropped.push({ index, field });
                }
            }
        } else {
            dropped.push({ index, field: null });
        }
    });
    return { list, dropped };
}



/**
 * The shape of the text between the app prefix and the suffix of a world-scoped storage key,
 * "<world prefix>[_<keystore>]": runs of letters, digits, "." and "-" joined by single underscores.
 * ONE rule for both sides: the settings-backup restore accepts only world keys of this shape
 * (ImportExportHelper's BACKUP_WORLD_KEY_REGEX is built from it), and main.js checks a ?keystore=
 * value against it, since a keystore outside it (a space, "café", "x:y", "_lead", "a__b") wrote keys
 * that no backup of that store could ever restore.
 * @constant {string}
 */
export const STORAGE_KEY_SEGMENTS_PATTERN = '[A-Za-z0-9.-]+(?:_[A-Za-z0-9.-]+)*';
const KEYSTORE_NAME_REGEX = new RegExp('^' + STORAGE_KEY_SEGMENTS_PATTERN + '$');
/** @constant {number} Longest ?keystore= name accepted (it is repeated in every key of that store) */
export const KEYSTORE_NAME_MAX_LENGTH = 64;

/**
 * Tells whether a keystore name (the ?keystore= url parameter, which picks a separate set of saved
 * state per case) can be used in storage keys a settings backup can restore.
 * @param {*} value - The name to check
 * @returns {boolean} True if it is a non-empty string of at most KEYSTORE_NAME_MAX_LENGTH
 *   characters that matches STORAGE_KEY_SEGMENTS_PATTERN
 */
export function isValidKeyStoreName(value) {
    return (typeof value === 'string') && (value.length <= KEYSTORE_NAME_MAX_LENGTH) && KEYSTORE_NAME_REGEX.test(value);
}





/**
 * Manages application state persistence using localStorage.
 * Supports both app-wide settings and world-specific (dataset-specific) settings.
 * @class
 */
export class StateManager {
    /**
     * Creates a new StateManager instance.
     * @param {Object} mapApp - The MapApp instance
     * @param {string} appStoragePrefix - Prefix for app-wide keys (e.g., 'cbmap:')
     */
    constructor(mapApp, appStoragePrefix) {
        this.mapApp = mapApp;
        this.appStoragePrefix = appStoragePrefix;
        //
        this.worldStoragePrefix = null;
        this.caseStoragePrefix = null;
        //
        this.relStorageKeyUi = 'ui';

        // Generate getter/setter methods from declarative definitions
        this._generateAccessors();
    }


    /**
     * Dynamically generates getter and setter methods from STATE_ACCESSORS definitions.
     * Creates methods like getTileLayerIndex() and setTileLayerIndex(value).
     * @private
     */
    _generateAccessors() {
        for (const { name, path, default: defaultValue } of STATE_ACCESSORS) {
            // Create getter: e.g., getTileLayerIndex()
            this[`get${name}`] = () => this.get(path, defaultValue);

            // Create setter: e.g., setTileLayerIndex(value)
            this[`set${name}`] = (value) => this.set(path, value);
        }
    }


    /**
     * Initializes the state manager with a world-specific storage prefix.
     * Must be called before using world-specific storage operations.
     * @param {string} worldStoragePrefix - World-specific suffix (e.g., 'nyn5')
     */
    initializeWithWorldStoragePrefix(worldStoragePrefix, caseStoragePrefix) {
        this._setWorldStoragePrefix(worldStoragePrefix)
        this._setCaseStoragePrefix(caseStoragePrefix);
        this.state = this.loadState();
    }


    // ==========================================
    // PREFIX KEY NAME ACCESSORS
    // ==========================================

    /**
     * Gets the world-specific storage prefix.
     * @returns {string} The world storage prefix
     * @throws {Error} If world storage prefix has not been set
     */
    getWorldPlusCaseStoragePrefix() {
        if (!this.worldStoragePrefix) {
            throw new Error('ERROR: worldStoragePrefix in StateManager has not been set.');
        }
        if (this.caseStoragePrefix) {
            return this.worldStoragePrefix + '_' + this.caseStoragePrefix;
        }
        return this.worldStoragePrefix;
    }

    /**
     * Sets the world storage prefix.
     * @param {string} val - World storage prefix value
     * @private
     */
    _setWorldStoragePrefix(val) {
        this.worldStoragePrefix = val;
    }


    getCaseStoragePrefix(val) {
        if (!this.caseStoragePrefix) {
            throw new Error('ERROR: caseStoragePrefix in StateManager has not been set.');
        }
        return this.caseStoragePrefix;
    }
    _setCaseStoragePrefix(val) {
        this.caseStoragePrefix = val;
    }


    /**
     * Gets the app-wide storage prefix.
     * @returns {string} The app storage prefix (e.g., 'cbmap:')
     */
    getStoragePrefixToApp() {
        return this.appStoragePrefix;
    }

    /**
     * Gets the full storage prefix including world suffix.
     * @returns {string} Combined prefix (e.g., 'cbmap::nynoir')
     */
    getFullStoragePrefixToWorld() {
        return this.appStoragePrefix + this.getWorldPlusCaseStoragePrefix() + '_';
    }

    /**
     * Adds the full storage prefix to a key.
     * @param {string} key - The relative key name
     * @returns {string} Full storage key with prefix
     */
    addFullStoragePrefix(key) {
        return this.appStoragePrefix + this.getWorldPlusCaseStoragePrefix() + '_' + key;
    }

    /**
     * Adds just the app storage prefix to a key.
     * @param {string} key - The relative key name
     * @returns {string} Full storage key with prefix
     */
    addAppStoragePrefix(key) {
        return this.appStoragePrefix + key;
    }




    // ==========================================
    // GENERIC ACCESSORS
    // ==========================================



    /**
     * Generic getter for nested state properties using dot-notation path
     * @param {string} path - Dot-notation path (e.g., 'sidebar.width')
     * @param {*} defaultValue - Default value if not found (default: null)
     * @returns {*} The value or default
     */
    get(path, defaultValue = null) {
        const keys = path.split('.');
        let obj = this.state;

        for (const key of keys) {
            if (obj === undefined || obj === null) return defaultValue;
            obj = obj[key];
        }

        return obj ?? defaultValue;
    }

    /**
     * Generic setter for nested state properties using dot-notation path
     * Automatically creates intermediate objects if they don't exist
     * @param {string} path - Dot-notation path (e.g., 'sidebar.width')
     * @param {*} value - Value to set
     */
    set(path, value) {
        // No world loaded yet (the first world config failed to load, so initializeWithWorldStoragePrefix
        // never ran): keep the change in memory on the default state; saveState() has nowhere to save it.
        if (!this.state) {
            this.state = this.getDefaultState();
        }
        const keys = path.split('.');
        let obj = this.state;

        // Navigate/create path to parent
        for (let i = 0; i < keys.length - 1; i++) {
            const key = keys[i];
            if (!obj[key]) {
                obj[key] = {};
            }
            obj = obj[key];
        }

        // Set the value
        obj[keys[keys.length - 1]] = value;
        this.saveState();
    }






    // ==========================================
    // UTIL FUNCS
    // ==========================================

    /**
     * Checks if localStorage is available in the current environment.
     * Useful for detecting private browsing or restricted contexts.
     * @static
     * @returns {boolean} True if localStorage is available and writable
     */
    static isAvailable() {
        try {
            const test = '__localStorage_test__';
            localStorage.setItem(test, test);
            localStorage.removeItem(test);
            return true;
        } catch (error) {
            return false;
        }
    }


    /**
     * Clears all localStorage keys that start with the given prefix.
     * Used for resetting app or world-specific state.
     * @param {string} prefix - The prefix to match (e.g., 'cbmap:')
     */
    clearStorageByPrefix(prefix) {
        try {
            const keysToRemove = [];
            for (let i = 0; i < localStorage.length; i++) {
                const key = localStorage.key(i);
                if (key && key.startsWith(prefix)) {
                    keysToRemove.push(key);
                }
            }
            keysToRemove.forEach(key => localStorage.removeItem(key));
        } catch (error) {
            console.error('ERROR: Failed to clear storage by prefix:', error);
        }
    }



    // ==========================================
    // MAIN FUNCS
    // ==========================================


    /**
     * Loads state from localStorage, falling back to defaults if not found.
     * @returns {Object} The saved state or default state
     * @private
     */
    loadState() {
        const storageKey = this.addFullStoragePrefix(this.relStorageKeyUi);
        let saved = null;
        try {
            saved = localStorage.getItem(storageKey);
        } catch (error) {
            console.error('ERROR: Failed to read state from localStorage:', error);
            return this.getDefaultState();
        }

        if (!saved) {
            return this.getDefaultState();
        }

        let parsed;
        try {
            parsed = JSON.parse(saved);
        } catch (error) {
            this._handleCorruptStoredState(storageKey, saved, error);
            return this.getDefaultState();
        }
        // Rebuilt on top of the defaults (sanitizeStoredUiState, the settings-backup import's own
        // rule): a stored object that is valid JSON but lacks a section -- no "map", say -- or holds a
        // wrongly typed value used to be returned as it was, and the first read of the missing part
        // (this.state.map.zoom) threw, so the world never loaded until the browser's storage was
        // cleared. A missing part now simply takes its default.
        const sanitized = sanitizeStoredUiState(parsed);
        if (!sanitized) {
            // valid JSON, but not a state object at all (a number, an array, null)
            this._handleCorruptStoredState(storageKey, saved, new Error('the stored value is not a state object'));
            return this.getDefaultState();
        }
        if (sanitized.droppedCount > 0) {
            // (unknown keys -- sanitized.unknown -- are left out silently: a setting a later version
            // stopped using is not an error)
            const details = sanitized.dropped.map(d => `${d.path} (${d.reason})`).join(', ');
            console.warn(`WARNING: ${sanitized.droppedCount} stored state value(s) at key "${storageKey}" had the wrong type and were replaced by their defaults: ${details}.`);
        }
        return sanitized.state;
    }


    /**
     * Handles a corrupt stored-state value: moves the raw bytes to a
     * timestamped backup key (so subsequent saveState calls don't clobber
     * the evidence), logs the failure with a preview, and surfaces a toast
     * warning. The backup lives at "<storageKey>__corrupt_<ISO>" for
     * inspection in DevTools.
     * @param {string} storageKey - The original key whose value failed to parse
     * @param {string} rawValue - The unparseable raw string
     * @param {Error} error - The JSON.parse error
     * @private
     */
    _handleCorruptStoredState(storageKey, rawValue, error) {
        const timestamp = new Date().toISOString().replace(/[:.]/g, '-');
        const backupKey = `${storageKey}__corrupt_${timestamp}`;
        let backedUp = false;
        try {
            localStorage.setItem(backupKey, rawValue);
            localStorage.removeItem(storageKey);
            backedUp = true;
        } catch (backupErr) {
            console.error('ERROR: Failed to back up corrupt state value:', backupErr);
        }

        const preview = rawValue.length > 200 ? rawValue.slice(0, 200) + '…' : rawValue;
        console.error(
            `ERROR: Stored state at key "${storageKey}" failed to parse as JSON ` +
            `(length=${rawValue.length}, parse error: ${error.message}).\n` +
            (backedUp
                ? `Raw value backed up to localStorage key "${backupKey}" for inspection.\n`
                : `Raw value could NOT be backed up (storage write failed).\n`) +
            `Preview: ${preview}`
        );

        // Defer the toast — loadState() runs early during init, before the
        // toast container is necessarily ready.
        setTimeout(() => {
            try {
                const msg = backedUp
                    ? `Saved app state was corrupt and could not be loaded. A copy was preserved at localStorage key "${backupKey}". Defaults have been restored.`
                    : `Saved app state was corrupt and could not be loaded. Defaults have been restored.`;
                showStatusWarning(msg, 15000);
            } catch (e) {
                // Toast plumbing not ready; the console.error above is sufficient.
            }
        }, 0);
    }

    /**
     * Gets the default state structure with initial values.
     * @returns {Object} Default state object with map, sidebar, help, and settings sections
     * @private
     */
    getDefaultState() {
        // (a module function: sanitizeStoredUiState starts from the same defaults)
        return buildDefaultUiState();
    }

    /**
     * Saves the current state to localStorage.
     * @private
     */
    saveState() {
        if (!this.worldStoragePrefix) {
            // no world loaded yet: world-scoped state has no storage key
            return;
        }
        const storageKey = this.addFullStoragePrefix(this.relStorageKeyUi);
        try {
            localStorage.setItem(storageKey, JSON.stringify(this.state));
        } catch (error) {
            console.error('ERROR: Failed to save state to localStorage:', error);
        }
    }




    // ==========================================
    // SPECIFIC STATE METHODS
    // ==========================================
    // Note: Simple getter/setter pairs are auto-generated from STATE_ACCESSORS.
    // The following methods have custom logic beyond simple get/set.

    // ==========================================
    // Map State
    // ==========================================

    /**
     * Update map view state (zoom and center)
     * @param {number} zoom - Current zoom level
     * @param {Array} center - Current center coordinates [lon, lat]
     */
    setMapView(zoom, center) {
        this.state.map.zoom = zoom;
        this.state.map.center = center;
        this.saveState();
    }

    /**
     * Get saved map view state
     * @returns {Object|null} Object with zoom and center, or null if not saved
     */
    getMapView() {
        const { zoom, center } = this.state.map;
        return (zoom !== null && center !== null) ? { zoom, center } : null;
    }

    // ==========================================
    // Overlay Visibility State
    // ==========================================

    /**
     * Sets the visibility state for a specific overlay layer.
     * @param {string} layerId - The layer identifier (white, yellow, reverse, advanced, bookmarks, history)
     * @param {boolean} visible - Whether the layer should be visible
     */
    setOverlayVisibility(layerId, visible) {
        this.set(`overlays.${layerId}`, visible);
    }

    /**
     * Gets the visibility state for a specific overlay layer.
     * @param {string} layerId - The layer identifier
     * @returns {boolean} Whether the layer is visible (default: true)
     */
    getOverlayVisibility(layerId) {
        return this.get(`overlays.${layerId}`, true);
    }

    /**
     * Sets the visibility state for all overlay layers.
     * @param {Object} state - Object with layer ids as keys and visibility booleans as values
     */
    setAllOverlayVisibility(state) {
        for (const [layerId, visible] of Object.entries(state)) {
            this.set(`overlays.${layerId}`, visible);
        }
    }

    /**
     * Gets the visibility state for all overlay layers.
     * @returns {Object} Object with layer ids as keys and visibility booleans as values
     */
    getAllOverlayVisibility() {
        return this.get('overlays', {
            white: false,
            yellow: false,
            reverse: false,
            advanced: false,
            bookmarks: true,
            history: false
        });
    }

    // ==========================================
    // APP-SCOPED STATE (uses appStoragePrefix, not worldStoragePrefix)
    // ==========================================

    /**
     * Gets the last loaded world ID from app-scoped storage.
     * This is stored at the app level so it can be read before any world is loaded.
     * @returns {string|null} The last loaded world ID, or null if not set
     */
    getLastWorldId() {
        const key = this.addAppStoragePrefix('lastWorldId');
        try {
            return localStorage.getItem(key);
        } catch (error) {
            console.error('ERROR: Failed to read lastWorldId from localStorage:', error);
            return null;
        }
    }

    /**
     * Saves the last loaded world ID to app-scoped storage.
     * @param {string} worldId - The world ID to save
     */
    setLastWorldId(worldId) {
        const key = this.addAppStoragePrefix('lastWorldId');
        try {
            localStorage.setItem(key, worldId);
        } catch (error) {
            console.error('ERROR: Failed to save lastWorldId to localStorage:', error);
        }
    }


    /**
     * Reads a JSON-serialized value from app-scoped storage (shared across all worlds).
     * @param {string} relKey - The relative key name (gets the app prefix applied)
     * @param {*} defaultValue - Value returned when the key is missing or unparseable
     * @returns {*} The parsed value or the default
     * @private
     */
    _getAppJson(relKey, defaultValue) {
        const key = this.addAppStoragePrefix(relKey);
        let raw = null;
        try {
            raw = localStorage.getItem(key);
        } catch (error) {
            console.error(`ERROR: Failed to read ${relKey} from localStorage:`, error);
            return defaultValue;
        }
        if (raw === null) { return defaultValue; }
        try {
            return JSON.parse(raw);
        } catch (error) {
            console.error(`ERROR: Failed to parse ${relKey} from localStorage:`, error);
            return defaultValue;
        }
    }

    /**
     * Writes a JSON-serialized value to app-scoped storage (shared across all worlds).
     * @param {string} relKey - The relative key name (gets the app prefix applied)
     * @param {*} value - The value to serialize and store
     * @private
     */
    _setAppJson(relKey, value) {
        const key = this.addAppStoragePrefix(relKey);
        try {
            localStorage.setItem(key, JSON.stringify(value));
        } catch (error) {
            console.error(`ERROR: Failed to save ${relKey} to localStorage:`, error);
        }
    }

    /**
     * Reads an app-scoped JSON value that must have a given shape; a stored value of any other shape
     * (hand-edited storage, or a value an older build wrote) is replaced by the default, with a warning.
     * @param {string} relKey - The relative key name (gets the app prefix applied)
     * @param {*} defaultValue - Value returned when the key is missing, unparseable or of the wrong shape
     * @param {function(*): boolean} isGood - Whether a parsed value has the right shape
     * @param {string} reason - What a wrong value is, for the warning (e.g. 'not true/false')
     * @returns {*} The stored value or the default
     * @private
     */
    _getAppJsonOfShape(relKey, defaultValue, isGood, reason) {
        const value = this._getAppJson(relKey, defaultValue);
        if (isGood(value)) {
            return value;
        }
        console.warn(`WARNING: stored value at key "${this.addAppStoragePrefix(relKey)}" was ${reason} and was replaced by its default.`);
        return defaultValue;
    }

    /**
     * Reads one of the app-scoped background music lists through sanitizeStoredMusicList (the
     * settings-backup import's own entry rule, minus its length limits), so a list that is not a list
     * reads as empty and a null / non-object / url-less entry is left out, with a warning, instead of
     * making the player's add/remove throw on it. (Extra fields of a kept entry are left out silently,
     * like unknown keys of the ui state.) The clean list is written back by the next add or remove.
     * @param {string} relKey - 'bgMusicUserTracks' or 'bgMusicPlaylists'
     * @param {string} textField - 'title' for tracks, 'label' for playlists
     * @returns {Array<Object>} The clean list
     * @private
     */
    _getAppMusicList(relKey, textField) {
        const storageKey = this.addAppStoragePrefix(relKey);
        const result = sanitizeStoredMusicList(this._getAppJson(relKey, []), textField);
        if (!result) {
            console.warn(`WARNING: stored value at key "${storageKey}" was not a list and was replaced by an empty list.`);
            return [];
        }
        const droppedEntries = result.dropped.filter(d => d.field === null).map(d => d.index + 1);
        if (droppedEntries.length > 0) {
            console.warn(`WARNING: ${droppedEntries.length} stored entr${droppedEntries.length === 1 ? 'y' : 'ies'} at key "${storageKey}" ${droppedEntries.length === 1 ? 'was' : 'were'} not a ${textField} and url and left out: entr${droppedEntries.length === 1 ? 'y' : 'ies'} ${droppedEntries.join(', ')}.`);
        }
        return result.list;
    }


    // ==========================================
    // Music Player (app-scoped / global, shared across worlds)
    // ==========================================
    // The player offers multiple playlists: a special non-deletable "User Playlist" of arbitrary
    // tracks (stored here as bgMusicUserTracks), plus any number of m3u playlists, each a {label, url}
    // pointing at an .m3u file (stored here as bgMusicPlaylists). Worlds contribute m3u playlists that
    // are merged into this known list on load. Everything here is global (shared across worlds).

    // --- User Playlist tracks (the one editable, serialized track list) ---

    /**
     * @returns {Array<{title: string, url: string}>} User Playlist tracks (local files are session-only and never stored)
     */
    getBackgroundMusicUserTracks() {
        return this._getAppMusicList('bgMusicUserTracks', 'title');
    }

    /**
     * @param {Array<{title: string, url: string}>} tracks
     */
    setBackgroundMusicUserTracks(tracks) {
        this._setAppJson('bgMusicUserTracks', tracks || []);
    }

    /**
     * Adds (or moves to the front, deduped by url) a User Playlist track. Local-file object urls should
     * NOT be passed here — they cannot be replayed across sessions and are kept in memory by the player.
     * @param {string} title
     * @param {string} url
     */
    addBackgroundMusicUserTrack(title, url) {
        if (!url || typeof url !== 'string') { return; }
        const trimmedUrl = url.trim();
        if (!trimmedUrl) { return; }
        let tracks = this.getBackgroundMusicUserTracks() || [];
        tracks = tracks.filter(t => t.url !== trimmedUrl);
        tracks.unshift({ title: (title || trimmedUrl), url: trimmedUrl });
        this.setBackgroundMusicUserTracks(tracks);
    }

    /**
     * Removes a User Playlist track by url.
     * @param {string} url
     */
    removeBackgroundMusicUserTrack(url) {
        let tracks = this.getBackgroundMusicUserTracks() || [];
        tracks = tracks.filter(t => t.url !== url);
        this.setBackgroundMusicUserTracks(tracks);
    }

    // --- Stay-and-loop toggle (loop the current song instead of advancing) ---

    /**
     * @returns {boolean} Whether the player should loop the current song instead of advancing
     */
    getBackgroundMusicStayAndLoop() {
        return this._getAppJsonOfShape('bgMusicStayAndLoop', false, (v) => typeof v === 'boolean', 'not true/false');
    }

    /**
     * @param {boolean} value
     */
    setBackgroundMusicStayAndLoop(value) {
        this._setAppJson('bgMusicStayAndLoop', !!value);
    }

    // --- Known m3u playlists (the user's persistent list of {label, url} m3u playlists) ---

    /**
     * @returns {Array<{label: string, url: string}>} The user's known m3u playlists
     */
    getMusicPlaylists() {
        return this._getAppMusicList('bgMusicPlaylists', 'label');
    }

    /**
     * @param {Array<{label: string, url: string}>} playlists
     */
    setMusicPlaylists(playlists) {
        this._setAppJson('bgMusicPlaylists', playlists || []);
    }

    /**
     * Adds an m3u playlist to the known list, deduped by url. If the url is already known its label is
     * updated (so a world can supply a nicer label later); order is otherwise preserved.
     * @param {string} label
     * @param {string} url
     */
    addMusicPlaylist(label, url) {
        if (!url || typeof url !== 'string') { return; }
        const trimmedUrl = url.trim();
        if (!trimmedUrl) { return; }
        const playlists = this.getMusicPlaylists() || [];
        const existing = playlists.find(p => p.url === trimmedUrl);
        if (existing) {
            if (label) { existing.label = label; }
        } else {
            playlists.push({ label: (label || trimmedUrl), url: trimmedUrl });
        }
        this.setMusicPlaylists(playlists);
    }

    /**
     * Removes an m3u playlist from the known list by url.
     * @param {string} url
     */
    removeMusicPlaylist(url) {
        let playlists = this.getMusicPlaylists() || [];
        playlists = playlists.filter(p => p.url !== url);
        this.setMusicPlaylists(playlists);
    }

    // --- Active (last-selected) playlist ---

    /**
     * @returns {string} The id of the last-selected playlist ('user' for the User Playlist, else an m3u url)
     */
    getMusicActivePlaylistId() {
        return this._getAppJsonOfShape('bgMusicActivePlaylist', 'user', (v) => typeof v === 'string', 'not a playlist id');
    }

    /**
     * @param {string} id
     */
    setMusicActivePlaylistId(id) {
        this._setAppJson('bgMusicActivePlaylist', id || 'user');
    }


    // ==========================================
    // PDF URL History
    // ==========================================

    /**
     * Adds a URL to the PDF URL history.
     * Moves the URL to the front if it already exists.
     * Limits history to maxItems entries.
     * @param {string} url - The URL to add
     * @param {number} [maxItems=10] - Maximum number of URLs to keep in history
     */
    addPdfUrlToHistory(url, maxItems = 10) {
        if (!url || typeof url !== 'string') return;

        const trimmedUrl = url.trim();
        if (!trimmedUrl) return;

        let history = this.getPdfUrlHistory() || [];

        // Remove if already exists (we'll add to front)
        history = history.filter(u => u !== trimmedUrl);

        // Add to front
        history.unshift(trimmedUrl);

        // Limit size
        if (history.length > maxItems) {
            history = history.slice(0, maxItems);
        }

        this.setPdfUrlHistory(history);
        this.setLastPdfUrl(trimmedUrl);
    }

    /**
     * Clears the PDF URL history.
     */
    clearPdfUrlHistory() {
        this.setPdfUrlHistory([]);
    }


    /**
     * Clears all saved state for this app (all keys starting with the app prefix).
     * Resets state to defaults.
     */
    clearStateApp() {
        // clear all keys
        this.clearStorageByPrefix(this.getStoragePrefixToApp());
        // get it
        this.state = this.getDefaultState();
    }



    calcApplicationStorageSpaceUsed() {
        let totalChars = 0;
        const prefix = this.getStoragePrefixToApp();
        try {
            for (let i = 0; i < localStorage.length; i++) {
                const key = localStorage.key(i);
                if (key && key.startsWith(prefix)) {
                    const value = localStorage.getItem(key) || '';
                    totalChars += key.length + value.length;
                }
            }
        } catch (error) {
            console.error('ERROR: Failed to calculate storage space:', error);
            return null;
        }
        // localStorage stores strings as UTF-16, so each character uses ~2 bytes
        return totalChars * 2;
    }

    calcApplicationStorageSpaceUsedAsNiceSizeString() {
        const size = this.calcApplicationStorageSpaceUsed();
        return formatSizeAsNiceSizeString(size);
    }


}